import { useMemo } from "react";
import type { AgentLogEntry } from "../services/tradingAgentsV2";

interface AgentTimelineProps {
  log?: AgentLogEntry[];
}

interface Row {
  agent: string;
  durationMs: number;
  offsetMs: number;
  inputSummary?: string;
  outputSummary?: string;
}

// Cycle through a small palette so adjacent bars stay visually distinct.
const BAR_COLORS = [
  "bg-primary-500",
  "bg-emerald-500",
  "bg-amber-500",
  "bg-sky-500",
  "bg-violet-500",
  "bg-rose-500",
  "bg-teal-500",
];

function parseMs(value?: string | null): number | null {
  if (!value) return null;
  const t = Date.parse(value);
  return Number.isFinite(t) ? t : null;
}

function formatDuration(ms: number): string {
  if (ms < 1000) return `${ms}ms`;
  return `${(ms / 1000).toFixed(ms < 10000 ? 2 : 1)}s`;
}

export function AgentTimeline({ log }: AgentTimelineProps) {
  const { rows, totalMs } = useMemo(() => {
    const entries = (log ?? []).filter((e) => e && e.agent);
    if (entries.length === 0) return { rows: [] as Row[], totalMs: 0 };

    const starts = entries
      .map((e) => parseMs(e.started_at))
      .filter((n): n is number => n != null);
    const minStart = starts.length > 0 ? Math.min(...starts) : 0;

    const built: Row[] = entries.map((e) => {
      const start = parseMs(e.started_at);
      const end = parseMs(e.ended_at);
      const durationMs =
        typeof e.duration_ms === "number" && e.duration_ms >= 0
          ? e.duration_ms
          : start != null && end != null
            ? Math.max(0, end - start)
            : 0;
      const offsetMs = start != null ? Math.max(0, start - minStart) : 0;
      return {
        agent: e.agent,
        durationMs,
        offsetMs,
        inputSummary: e.input_summary,
        outputSummary: e.output_summary,
      };
    });

    const span = Math.max(
      1,
      ...built.map((r) => r.offsetMs + r.durationMs)
    );
    return { rows: built, totalMs: span };
  }, [log]);

  if (rows.length === 0) {
    return (
      <p className="text-sm text-gray-500">No agent timeline for this run.</p>
    );
  }

  return (
    <div className="space-y-2" aria-label="Agent execution timeline">
      {rows.map((row, i) => {
        const widthPct = Math.max(2, (row.durationMs / totalMs) * 100);
        const leftPct = (row.offsetMs / totalMs) * 100;
        const color = BAR_COLORS[i % BAR_COLORS.length];
        const tooltip = [
          `${row.agent} · ${formatDuration(row.durationMs)}`,
          row.inputSummary ? `in: ${row.inputSummary}` : "",
          row.outputSummary ? `out: ${row.outputSummary}` : "",
        ]
          .filter(Boolean)
          .join("\n");
        return (
          <div key={`${row.agent}-${i}`} className="flex items-center gap-2">
            <div className="w-28 shrink-0 truncate text-right text-xs font-medium capitalize text-gray-600">
              {row.agent.replace(/_/g, " ")}
            </div>
            <div className="relative h-5 flex-1 rounded bg-gray-100">
              <div
                className={`absolute top-0 flex h-5 items-center justify-end rounded ${color} px-1.5`}
                style={{ left: `${leftPct}%`, width: `${widthPct}%` }}
                title={tooltip}
              >
                <span className="truncate text-[10px] font-medium text-white">
                  {formatDuration(row.durationMs)}
                </span>
              </div>
            </div>
          </div>
        );
      })}
      <div className="flex items-center gap-2 pt-1">
        <div className="w-28 shrink-0" />
        <div className="flex-1 text-right text-[10px] text-gray-400">
          total {formatDuration(totalMs)}
        </div>
      </div>
    </div>
  );
}
